import { YEARLY_PRICE_PER_MONTH } from "@/config/pricing";
import { MONTHLY_PRICE } from "@/config/pricing";
import { YEARLY_PRICE } from "@/config/pricing";
import type { IOSProduct } from "@/lib/types";

// Get the user's locale from window.navigator.language, fallback to en-US if not available
const getLocale = () => {
  if (typeof window === "undefined") return "en-US";

  const locale = window?.navigator?.language;
  // If the locale contains @ or other invalid characters, fall back to en-US
  if (!locale || locale.includes("@")) return "en-US";

  // Normalize the locale by replacing underscores with hyphens
  const normalizedLocale = locale.replace(/_/g, "-");

  // Ensure we have a valid locale format (e.g., en-US, de-DE, etc.)
  const [language, region] = normalizedLocale.split("-");
  if (!language || !region) return "en-US";

  return normalizedLocale;
};

// Get the locale to use for formatting, prioritizing product locale if available
const getFormattingLocale = (product: IOSProduct | null) => {
  if (product?.attributes.icuLocale) {
    // Extract just the locale part before any @ symbol
    const locale = product.attributes.icuLocale.split("@")[0];
    // Normalize the locale by replacing underscores with hyphens
    return locale.replace(/_/g, "-");
  }
  return getLocale();
};

export const formatPrice = (price: number, currencySymbol: string, locale?: string): string => {
  // Use Intl.NumberFormat to handle locale-specific formatting
  const formatter = new Intl.NumberFormat(locale || getLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currencySymbol}${formatter.format(price)}`;
};

export const roundMonthlyPriceTo99 = (monthlyPrice: number): string => {
  let roundedPrice = monthlyPrice.toFixed(2);
  if (roundedPrice.endsWith(".00")) {
    const basePrice = Number.parseFloat(roundedPrice);
    roundedPrice = (basePrice - 0.01).toFixed(2);
  }
  return roundedPrice;
};

export const getPriceDisplayIOS = (
  isYearly: boolean,
  yearlyProduct: IOSProduct | null,
  monthlyProduct: IOSProduct | null,
) => {
  const product = isYearly ? yearlyProduct : monthlyProduct;
  const offer = product?.attributes.offers[0];
  const price = offer?.priceFormatted || "€0";

  // Extract currency symbol from the formatted price
  const currencySymbol = price.replace(/[0-9.,\s-]/g, "");

  // Get the locale from the product or fall back to user's locale
  const locale = getFormattingLocale(product);

  // Calculate monthly price using the product's locale
  const monthlyPrice = isYearly ? calculateMonthlyPrice(price, currencySymbol, locale) : price;

  return {
    price: monthlyPrice,
    currency: offer?.currencyCode || "EUR",
    period: "month",
    monthlyPrice: isYearly ? price : null,
  };
};

export const getPriceDisplayAndroid = (
  isYearly: boolean,
  products: {
    monthly: { price: { formattedPrice?: string; currency: string; value: string } };
    yearly: { price: { formattedPrice?: string; currency: string; value: string } };
  },
  t: (key: string) => string,
) => {
  if (isYearly) {
    return {
      price: products.yearly.price.formattedPrice
        ? products.yearly.price.formattedPrice.replace(
            /[\d.,]+/,
            (Number(products.yearly.price.value) / 12).toFixed(2),
          )
        : `${products.yearly.price.currency} ${products.yearly.price.value}`,
      period: t("pro.monthly"),
      monthlyPrice:
        products.yearly.price.formattedPrice ||
        `${products.yearly.price.currency} ${(Number(products.yearly.price.value) * 12).toFixed(2)}`,
    };
  }

  return {
    price:
      products.monthly.price.formattedPrice ||
      `${products.monthly.price.currency} ${products.monthly.price.value}`,
    period: t("pro.monthly"),
  };
};

export const getPriceDisplayWeb = (isYearly: boolean) => {
  // Hardcoded pricing values (these should match the values in getTranslatedPricing)
  const yearlyPrice = Number(YEARLY_PRICE.replace(",", "."));
  const monthlyPrice = Number(MONTHLY_PRICE.replace(",", "."));
  const yearlyPricePerMonth = Number(YEARLY_PRICE_PER_MONTH.replace(",", "."));
  const currencySymbol = "€";
  const locale = getLocale();

  // Format the prices using the same formatting functions
  const formattedMonthlyPrice = formatPrice(monthlyPrice, currencySymbol, locale);
  const formattedYearlyPrice = formatPrice(yearlyPrice, currencySymbol, locale);
  const formattedYearlyPricePerMonth = formatPrice(yearlyPricePerMonth, currencySymbol, locale);

  return {
    price: isYearly ? formattedYearlyPricePerMonth : formattedMonthlyPrice,
    currency: "EUR",
    period: "month",
    monthlyPrice: isYearly ? formattedYearlyPrice : null,
  };
};

export const calculateMonthlyPrice = (
  yearlyPrice: string,
  currencySymbol: string,
  locale?: string,
): string => {
  // Remove currency symbol and normalize decimal separator to dot
  const normalizedPrice = yearlyPrice
    .replace(/[^0-9.,-]+/g, "") // Remove everything except numbers, dots, commas and minus
    .replace(/,(?=\d{3})/g, "") // Remove thousand separators (commas followed by 3 digits)
    .replace(/,/g, "."); // Convert remaining commas to dots for decimal points
  const yearlyPriceNumber = Number.parseFloat(normalizedPrice);

  // Calculate monthly price and round to .99
  const monthlyPriceNumber = yearlyPriceNumber / 12;
  const roundedMonthlyPrice = Number.parseFloat(roundMonthlyPriceTo99(monthlyPriceNumber));

  // Get the user's locale if not provided
  const userLocale = locale || getLocale();

  // Special handling for different currencies
  if (currencySymbol === "₹") {
    // For rupees, use Indian number formatting (e.g., 1,00,000) if locale is en-IN
    // Otherwise use the user's locale
    const formatter = new Intl.NumberFormat(userLocale === "en-IN" ? "en-IN" : userLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currencySymbol}${formatter.format(roundedMonthlyPrice)}`;
  }

  // For all other currencies, use user's locale
  const formatter = new Intl.NumberFormat(userLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Handle special cases where the currency symbol needs a space
  const spacedCurrencies = [
    "BGN",
    "PEN",
    "QAR",
    "SAR",
    "TZS",
    "CHF",
    "AED",
    "PLN",
    "RON",
    "DKK",
    "SEK",
    "MYR",
    "kr",
    "Kč",
    "Ft",
    "R",
    "lei",
    "zł",
  ];

  if (spacedCurrencies.includes(currencySymbol)) {
    return `${currencySymbol} ${formatter.format(roundedMonthlyPrice)}`;
  }

  return `${currencySymbol}${formatter.format(roundedMonthlyPrice)}`;
};

export const calculateYearlyDiscount = (monthlyPrice: number, yearlyPrice: number): number => {
  const monthlyTotal = monthlyPrice * 12;
  const discount = ((monthlyTotal - yearlyPrice) / monthlyTotal) * 100;
  return Math.round(discount);
};

export const calculateDailyPrice = (
  priceDisplay: string,
  isYearly: boolean,
  locale?: string,
): string => {
  // Extract just the numeric part from the price string
  // First, replace comma with dot for decimal separator to ensure proper numeric conversion
  const priceStr = priceDisplay
    .replace(/[^0-9.,-]+/g, "") // Remove everything except numbers, dots, commas and minus
    .replace(/,(?=\d{3})/g, "") // Remove thousand separators (commas followed by 3 digits)
    .replace(/,/g, "."); // Convert remaining commas to dots for decimal points

  const price = Number(priceStr);

  // Calculate daily price
  const dailyPrice = isYearly ? (price * 12) / 365 : price / 30;

  // Format the daily price with the same currency symbol
  const currencySymbol = priceDisplay.replace(/[0-9.,\s-]/g, "");
  return formatPrice(dailyPrice, currencySymbol, locale);
};
